import React, { useState, useEffect, useMemo } from 'react';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { Plus, Edit, Trash2, Search, Globe, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Country, CountryFormData } from '@/types/geography';
import { useConfirmDialog, createDeleteConfirm } from '@/components/ui/confirm-dialog';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { SortableTableHeader, sortData, SortDirection } from '@/components/ui/sortable-table-header';
import {
  useCountries as useCountriesApi,
  useCountry as useCountryApi,
  useCreateCountry,
  useUpdateCountry,
  useDeleteCountry,
} from '@/api-services/hooks/useCountryRefactored';
import {
  buildCountryPayload,
  mapCountryMasterToCountry,
} from '@/utils/geographyMappers';

const CountriesPage: React.FC = () => {
  const can = usePermissionCheck();
  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Sorting state
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Form state
  const [formData, setFormData] = useState<CountryFormData>({
    name: '',
    code: '',
    status: 'active'
  });

  // Validation errors state
  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    status?: string;
  }>({});

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Country name is required';
    } else {
      // Check for unique name (case-insensitive)
      const isDuplicateName = countries.some(
        country => country.name.toLowerCase() === formData.name.toLowerCase() &&
        country.id !== editingCountry?.id
      );
      if (isDuplicateName) {
        newErrors.name = 'Country with this name already exists';
      }
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Country code is required';
    } else if (formData.code.length !== 2) {
      newErrors.code = 'Country code must be exactly 2 characters';
    } else {
      // Check for unique code (case-insensitive)
      const isDuplicateCode = countries.some(
        country => country.code.toLowerCase() === formData.code.toLowerCase() &&
        country.id !== editingCountry?.id
      );
      if (isDuplicateCode) {
        newErrors.code = 'Country with this code already exists';
      }
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation on input change
  const handleInputChange = (field: keyof CountryFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Debounce search term (same as Base Pricing: reset page when debounced value updates)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length > 2;

  const countryQueryParams = useMemo(() => ({
    page: 0,
    size: 1000,
    ...(shouldSearch && { search: debouncedSearchTerm.trim() }),
  }), [shouldSearch, debouncedSearchTerm]);

  const {
    data: countryResponse,
    loading: countriesLoading,
    error: countriesError,
    refetch: refetchCountries,
  } = useCountriesApi(countryQueryParams);

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!countriesLoading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [countriesLoading]);

  const createCountryMutation = useCreateCountry();
  const updateCountryMutation = useUpdateCountry();
  const deleteCountryMutation = useDeleteCountry();

  // Fetch existing country data when editing to preserve system IP fields
  const { data: existingCountryData } = useCountryApi(
    editingCountry ? Number(editingCountry.id) : 0,
    {
      enabled: !!editingCountry && !!editingCountry.id,
    }
  );

  const actionLoading =
    createCountryMutation.loading ||
    updateCountryMutation.loading ||
    deleteCountryMutation.loading;

  useEffect(() => {
    if (countryResponse?.content) {
      const mapped = countryResponse.content.map(mapCountryMasterToCountry);
      setCountries(mapped);
    } else {
      setCountries([]);
    }
  }, [countryResponse]);

  useEffect(() => {
    if (countriesError) {
      toast.error(countriesError.message);
    }
  }, [countriesError]);

  // Filter countries by status and sort only (search is done by API when shouldSearch, like Base Pricing)
  useEffect(() => {
    let filtered = countries;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(country => country.status === statusFilter);
    }

    // Apply sorting
    filtered = sortData(filtered, sortKey, sortDirection);

    setFilteredCountries(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [countries, statusFilter, sortKey, sortDirection]);

  // Pagination calculations
  const totalItems = filteredCountries.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCountries = filteredCountries.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      status: 'active'
    });
    setErrors({});
    setEditingCountry(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      if (editingCountry) {
        const payload = await buildCountryPayload(formData, editingCountry.id, existingCountryData || undefined);
        await updateCountryMutation.mutate({
          id: Number(editingCountry.id),
          data: payload,
        });
        toast.success('Country updated successfully!');
        refetchCountries();
      } else {
        const payload = await buildCountryPayload(formData);
        await createCountryMutation.mutate(payload);
        toast.success('Country created successfully!');
        // Refresh list after create
        refetchCountries();
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save country';

      // Handle server-side validation errors for duplicates
      if (message.toLowerCase().includes('duplicate') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('unique')) {
        if (message.toLowerCase().includes('name')) {
          setErrors(prev => ({ ...prev, name: 'Country name already exists' }));
        } else if (message.toLowerCase().includes('code')) {
          setErrors(prev => ({ ...prev, code: 'Country code already exists' }));
        } else {
          // Generic duplicate error
          setErrors(prev => ({
            ...prev,
            name: 'Duplicate country detected',
            code: 'Duplicate country detected'
          }));
        }
      }

      toast.error(message);
    }
  };

  const handleEdit = (country: Country) => {
    setEditingCountry(country);
    setFormData({
      name: country.name,
      code: country.code,
      status: country.status,
      metadata: country.metadata || {}
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (country: Country) => {
    const countryName = country.name;
    confirm(createDeleteConfirm(
      'Country',
      async () => {
        try {
          await deleteCountryMutation.mutate(Number(country.id));
          toast.success(`${countryName} deleted successfully!`);
          refetchCountries();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete country';
          toast.error(message);
        }
      },
      `Are you sure you want to delete ${countryName}? This will permanently remove the country and all associated data. This action cannot be undone.`
    ));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Countries</h1>
          <p className="text-muted-foreground mt-1">Manage countries and their configurations</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {can('Create Countries') && (
              <DialogTrigger asChild>
                <Button className="bg-gradient-primary hover:opacity-90" onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Country
                </Button>
              </DialogTrigger>
            )}
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingCountry ? 'Edit Country' : 'Add New Country'}
              </DialogTitle>
              <DialogDescription>
                {editingCountry
                  ? 'Update the country information below.'
                  : 'Add a new country to the system.'
                }
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Country Name */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Country Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="name"
                  placeholder="e.g., United States"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Country Code */}
              <div className="space-y-2">
                <label htmlFor="code" className="text-sm font-medium">
                  Country Code <span className="text-red-500">*</span>
                </label>
                <Input
                  id="code"
                  placeholder="e.g., US (2 characters)"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value.toUpperCase().slice(0, 2))}
                  maxLength={2}
                  className={errors.code ? 'border-red-500' : ''}
                />
                {errors.code && (
                  <p className="text-sm text-red-500">{errors.code}</p>
                )}
              </div>

              {/* Status */}
              <div className="space-y-2">
                <label htmlFor="status" className="text-sm font-medium">
                  Status <span className="text-red-500">*</span>
                </label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger id="status" className={errors.status ? 'border-red-500' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-500">{errors.status}</p>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : editingCountry ? 'Update Country' : 'Add Country'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search countries... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                autoComplete="off"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Countries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Countries ({countriesLoading ? '...' : filteredCountries.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableTableHeader
                      label="Country Name"
                      sortKey="name"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableTableHeader
                      label="Code"
                      sortKey="code"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableTableHeader
                      label="Status"
                      sortKey="status"
                      currentSortKey={sortKey}
                      currentSortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  {(can('Edit Countries') || can('Delete Countries')) && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {countriesLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Loading countries...
                    </TableCell>
                  </TableRow>
                ) : paginatedCountries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {filteredCountries.length === 0
                        ? 'No countries found'
                        : 'No countries match the current filters'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCountries.map((country) => (
                    <TableRow key={country.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="font-medium">{country.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-mono uppercase">{country.code}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={country.status === 'active' ? 'default' : 'secondary'}
                          className={country.status === 'active' ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'}
                        >
                          {country.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {(can('Edit Countries') || can('Delete Countries')) && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {can('Edit Countries') && (
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(country)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {can('Delete Countries') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(country)}
                                disabled={actionLoading}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {!countriesLoading && totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default CountriesPage;
